import { Command } from 'commander'
import { createApiClient } from '../http.js'
import { marketplace } from '../api/catalog.js'
import { call, pageItems, pagingQuery, type PagedResult, type PagingFlags } from '../api/client.js'
import { requireMyPublisher, resolvePackageRef } from '../registry.js'
import { output, outputRecord, printJson } from '../output.js'

export function registerPackage(program: Command): void {
  const pkgCmd = program.command('package').description('Manage marketplace packages')

  pkgCmd
    .command('list')
    .description("List your publisher's packages")
    .option('--json', 'output JSON')
    .action(async (options: { json?: boolean }) => {
      const api = await createApiClient()
      const publisher = await requireMyPublisher(api)
      const page = await call<PagedResult<Record<string, unknown>>>(api, marketplace.listPublisherPackages, {
        params: { publisherId: publisher.id },
        query: { PageNo: 1, PageSize: 100 },
      })
      output(pageItems(page), { name: 'Name', kind: 'Kind', visibility: 'Visibility', latestPublishedVersion: 'Latest', id: 'Id' }, options, page)
    })

  pkgCmd
    .command('info <ref>')
    .description("Show package details (ref: 'name' or 'publisher/name')")
    .option('--json', 'output JSON')
    .action(async (ref: string, options: { json?: boolean }) => {
      const api = await createApiClient()
      const { package: pkg } = await resolvePackageRef(api, ref)
      const detail = await call<Record<string, unknown>>(api, marketplace.getPackage, { params: { id: pkg.id } })
      outputRecord(detail, options)
    })

  pkgCmd
    .command('versions <ref>')
    .description('List versions of a package')
    .option('--json', 'output JSON')
    .action(async (ref: string, options: { json?: boolean }) => {
      const api = await createApiClient()
      const { package: pkg } = await resolvePackageRef(api, ref)
      const page = await call<PagedResult<Record<string, unknown>>>(api, marketplace.listVersions, {
        params: { packageId: pkg.id },
        query: { PageNo: 1, PageSize: 100 },
      })
      output(pageItems(page), { version: 'Version', status: 'Status', sizeBytes: 'Size', hasMarketplaceSignature: 'Countersigned', id: 'Id' }, options, page)
    })

  pkgCmd
    .command('update <ref>')
    .description('Update package metadata')
    .option('--display-name <displayName>')
    .option('--description <description>')
    .option('--icon <icon>')
    .option('--categories <category...>', 'category list (replaces existing)')
    .action(async (ref: string, options: { displayName?: string; description?: string; icon?: string; categories?: string[] }) => {
      const api = await createApiClient()
      const { package: pkg } = await resolvePackageRef(api, ref)
      const current = await call<Record<string, unknown>>(api, marketplace.getPackage, { params: { id: pkg.id } })
      await call(api, marketplace.updatePackage, {
        params: { id: pkg.id },
        body: {
          id: pkg.id,
          displayName: options.displayName ?? current.displayName,
          description: options.description ?? current.description ?? null,
          icon: options.icon ?? current.icon ?? null,
          categories: options.categories ?? current.categories ?? [],
        },
      })
      console.log(`Package ${ref} updated.`)
    })

  pkgCmd
    .command('deprecate <ref>')
    .description('Mark a package as deprecated (optionally with a successor)')
    .option('--message <message>', 'deprecation message shown in the catalog')
    .option('--replacement <packageId>', 'suggested replacement package id')
    .action(async (ref: string, options: { message?: string; replacement?: string }) => {
      const api = await createApiClient()
      const { package: pkg } = await resolvePackageRef(api, ref)
      await call(api, marketplace.deprecatePackage, {
        params: { id: pkg.id },
        body: { id: pkg.id, message: options.message ?? null, replacementPackageId: options.replacement ?? null },
      })
      console.log(`Package ${ref} deprecated.`)
    })

  pkgCmd
    .command('undeprecate <ref>')
    .description('Remove the deprecation flag from a package')
    .action(async (ref: string) => {
      const api = await createApiClient()
      const { package: pkg } = await resolvePackageRef(api, ref)
      await call(api, marketplace.undeprecatePackage, { params: { id: pkg.id } })
      console.log(`Package ${ref} restored.`)
    })

  pkgCmd
    .command('archive <ref>')
    .description('Archive (soft-delete) a package — it disappears from the catalog')
    .action(async (ref: string) => {
      const api = await createApiClient()
      const { package: pkg } = await resolvePackageRef(api, ref)
      await call(api, marketplace.archivePackage, { params: { id: pkg.id } })
      console.log(`Package ${ref} archived.`)
    })

  pkgCmd
    .command('stats <ref>')
    .description('Show download/install statistics for a package')
    .action(async (ref: string) => {
      const api = await createApiClient()
      const { package: pkg } = await resolvePackageRef(api, ref)
      const stats = await call<unknown>(api, marketplace.getPackageStats, { params: { packageId: pkg.id } })
      printJson(stats)
    })

  const visibilityCmd = pkgCmd.command('visibility').description('Manage private-package visibility')

  visibilityCmd
    .command('set <ref> <visibility>')
    .description('Set catalog visibility: Public | Private')
    .action(async (ref: string, visibility: string) => {
      const api = await createApiClient()
      const { package: pkg } = await resolvePackageRef(api, ref)
      await call(api, marketplace.setPackageVisibility, { params: { id: pkg.id }, body: { id: pkg.id, visibility } })
      console.log(`Package ${ref} visibility → ${visibility}.`)
    })

  visibilityCmd
    .command('grants <ref>')
    .description('List visibility grants of a private package')
    .option('--json', 'output JSON')
    .action(async (ref: string, options: { json?: boolean }) => {
      const api = await createApiClient()
      const { package: pkg } = await resolvePackageRef(api, ref)
      const grants = await call<Record<string, unknown>[]>(api, marketplace.listVisibilityGrants, { params: { packageId: pkg.id } })
      output(grants ?? [], { granteeType: 'Type', granteeId: 'Grantee', id: 'GrantId' }, options, grants)
    })

  visibilityCmd
    .command('grant <ref> <granteeId>')
    .description('Grant a workspace/account visibility of a private package')
    .option('--type <granteeType>', 'Workspace | Account', 'Workspace')
    .action(async (ref: string, granteeId: string, options: { type: string }) => {
      const api = await createApiClient()
      const { package: pkg } = await resolvePackageRef(api, ref)
      await call(api, marketplace.grantVisibility, {
        params: { packageId: pkg.id },
        body: { packageId: pkg.id, granteeType: options.type, granteeId },
      })
      console.log(`Visibility of ${ref} granted to ${options.type} ${granteeId}.`)
    })

  visibilityCmd
    .command('revoke <ref> <grantId>')
    .description('Revoke a visibility grant')
    .action(async (ref: string, grantId: string) => {
      const api = await createApiClient()
      const { package: pkg } = await resolvePackageRef(api, ref)
      await call(api, marketplace.revokeVisibility, { params: { packageId: pkg.id, grantId } })
      console.log(`Visibility grant ${grantId} revoked.`)
    })

  program
    .command('installs')
    .description('List package installations in a workspace')
    .option('--workspace <workspaceId>')
    .option('--page <n>')
    .option('--page-size <n>')
    .option('--json', 'output JSON')
    .action(async (options: PagingFlags & { workspace?: string; json?: boolean }) => {
      const api = await createApiClient()
      const page = await call<PagedResult<Record<string, unknown>>>(api, marketplace.listInstallations, {
        query: { workspaceId: options.workspace, ...pagingQuery(options) },
      })
      output(pageItems(page), { packageName: 'Package', version: 'Version', status: 'Status', installPath: 'Path', id: 'Id' }, options, page)
    })
}
